import useSWR, { keyInterface, ConfigInterface } from 'swr';
import qs, { IStringifyOptions } from 'qs';
import { defaultKyOptions } from './ky';
import { FetcherOptions, KyOptions } from '.';
import fetcher from './swr-fetcher-ky';
import { WordpressError } from '../models/wordpress/WordpressError';
import { GIKApiError } from '../models/gik/APIError';

const kyDefaults: KyOptions = {
  ...defaultKyOptions,
  retry: {
    limit: 0, // disable retries on KY, SWR will do the retries.
  },
};

const swrDefaults: ConfigInterface = {
  refreshInterval: 0,
  // auto revalidate when window gets focused
  revalidateOnFocus: false,
  // enable or disable automatic revalidation when component is mounted
  // (by default revalidation occurs on mount when initialData is not set, use this flag to force behavior)
  revalidateOnMount: undefined,
  // automatically revalidate when the browser regains a network connection (via navigator.onLine)
  revalidateOnReconnect: false,
  // polling when the window is invisible
  refreshWhenOffline: false,
  // polling when the browser is offline
  refreshWhenHidden: false,
  // retry on errors (including timeouts)
  shouldRetryOnError: true,
};

/**
 * SWR Hook for querying the Give Inkind API
 *
 * @param key    a unique key string for the request (or a function / array / null)
 * @param config (optional) a Promise returning function to fetch your data
 * @param opts   options for the Ky fetcher instantiator
 */
export function useApi<T, Error = GIKApiError>(
  key: keyInterface,
  config: ConfigInterface = {},
  opts: FetcherOptions = {}
) {
  const fetcherOpts: FetcherOptions = {
    prefixUrl: process.env.DOTNET_API_URL,
    ...kyDefaults,
    useCache: false,
    ...opts,
  };

  const useSuspense = process.env.SUSPENSE_ENABLED === 'true' ? true : false;
  if (config?.suspense) {
    config.suspense = useSuspense;
  }

  return useSWR<T, Error>(
    key ? parseUrl(key, fetcherOpts) : null,
    fetcher<T>(fetcherOpts),
    Object.assign({}, swrDefaults, config)
  );
}

/**
 * SWR Hook for querying the Give Inkind Wordpress API
 *
 * @param key    a unique key string for the request (or a function / array / null)
 * @param config (optional) a Promise returning function to fetch your data
 * @param opts   options for the Ky fetcher instantiator
 */
export function useWPApi<T, E = WordpressError>(
  key: keyInterface,
  config: ConfigInterface = {},
  opts: FetcherOptions = {}
) {
  const fetcherOpts = {
    prefixUrl: process.env.WP_API_URL,
    useCache: true,
    ...kyDefaults,
    ...opts,
  };

  const useSuspense = process.env.SUSPENSE_ENABLED === 'true' ? true : false;
  if (config?.suspense) {
    config.suspense = useSuspense;
  }

  const response = useSWR(
    key ? parseUrl(key, fetcherOpts) : null,
    fetcher<T>(fetcherOpts),
    Object.assign({}, swrDefaults, config)
  );

  // handle 200 responses that are not actually OK responses
  if (response.data && response.data.statusCode && response.data.statusCode !== 200) {
    // return an error responseInterface instead
    return {
      data: undefined,
      error: response.data,
      revalidate: response.revalidate,
      mutate: response.mutate,
      isValidating: response.isValidating,
    };
  }

  return response;
}

/**
 * SWR Hook for querying the Give Inkind ACF V3 Wordpress API
 *
 * @param key    a unique key string for the request (or a function / array / null)
 * @param config (optional) a Promise returning function to fetch your data
 * @param opts   options for the Ky fetcher instantiator
 */
export function useWPAcfV3Api<T, E = WordpressError>(
  key: keyInterface,
  config: ConfigInterface = {},
  opts: FetcherOptions = {}
) {
  const fetcherOpts = {
    prefixUrl: process.env.WP_ACF_V3_API_URL,
    useCache: true,
    ...kyDefaults,
    ...opts,
  };

  const useSuspense = process.env.SUSPENSE_ENABLED === 'true' ? true : false;
  if (config?.suspense) {
    config.suspense = useSuspense;
  }

  const response = useSWR(
    key ? parseUrl(key, fetcherOpts) : null,
    fetcher<T>(fetcherOpts),
    Object.assign({}, swrDefaults, config)
  );

  // handle 200 responses that are not actually OK responses
  if (response.data && response.data.statusCode && response.data.statusCode !== 200) {
    // return an error responseInterface instead
    return {
      data: undefined,
      error: response.data,
      revalidate: response.revalidate,
      mutate: response.mutate,
      isValidating: response.isValidating,
    };
  }

  return response;
}

/**
 * SWR Hook for querying the Give Inkind WooCommerce API
 *
 * @param key    a unique key string for the request (or a function / array / null)
 * @param config (optional) a Promise returning function to fetch your data
 * @param opts   options for the Ky fetcher instantiator
 */
export function useWCApi<T>(key: keyInterface, config: ConfigInterface = {}, opts: FetcherOptions = {}) {
  const fetcherOpts = {
    prefixUrl: process.env.WC_API_URL,
    useCache: false,
    ...kyDefaults,
    headers: {},
    ...opts,
  };

  const useSuspense = process.env.SUSPENSE_ENABLED === 'true' ? true : false;
  if (config?.suspense) {
    config.suspense = useSuspense;
  }

  const response = useSWR(
    key ? parseUrl(key, fetcherOpts) : null,
    fetcher<T>(fetcherOpts),
    Object.assign({}, swrDefaults, config)
  );

  // handle 200 responses that are not actually OK responses
  if (response.data && response.data.statusCode && response.data.statusCode !== 200) {
    // return an error responseInterface instead
    return {
      data: undefined,
      error: response.data,
      revalidate: response.revalidate,
      mutate: response.mutate,
      isValidating: response.isValidating,
    };
  }

  return response;
}

/**
 * Parse an url with prefix and support for querying through the cache server.
 *
 * @param key     a unique key string for the request (or a function / array / null)
 * @param options options for the Ky fetcher instantiator
 */
export function parseUrl(key: keyInterface, options: FetcherOptions = {}) {
  // assemble final url with search params so SWR can use it as a cache key
  const params = options.searchParams || options.deepQueryParams;
  const qsStringifyOptions: IStringifyOptions = { arrayFormat: 'comma', ...options.qsStringifyOptions };
  const qsParams: string = params ? qs.stringify(params, qsStringifyOptions) : '';
  let url: string = key.toString();

  if (qsParams) url += `?${qsParams}`;
  if (options.prefixUrl) url = options.prefixUrl + '/' + url;
  // TODO: revisit below - logs are spammed multiple times
  // info('SWR parseUrl', key, JSON.stringify(params, null, 2));

  const cacheQuerystring = options.withAllPages ? '?proxy-cms-pages=all' : '';
  return options.useCache ? `${process.env.API_CACHE_URL}/url/${encodeURIComponent(url)}${cacheQuerystring}` : url;
}
